import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Uploader, { MultiUploader } from "@/components/Uploader";
import EBib from "@/components/EBib";
import ECertificate from "@/components/ECertificate";
import { api, formatIDR, formatSeconds, parseHMS, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { downloadElementAsPng } from "@/lib/download";
import { CheckCircle, Clock, XCircle, DownloadSimple, Timer, Package } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    pending: { c: "status-pending", label: "PENDING" },
    verified: { c: "status-verified", label: "VERIFIED" },
    rejected: { c: "status-rejected", label: "REJECTED" },
    awaiting_proof: { c: "status-awaiting", label: "AWAITING PROOF" },
    not_submitted: { c: "status-awaiting", label: "NOT SUBMITTED" },
  };
  const s = map[status] || { c: "status-awaiting", label: status?.toUpperCase() };
  return <span className={`status-badge ${s.c}`} data-testid={`status-${status}`}>{s.label}</span>;
}

function ScaledPreview({ width, height, children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={ref} className="relative w-full overflow-hidden" style={{ height: Math.ceil(height * scale) }}>
      <div className="absolute top-0 left-0" style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [sp] = useSearchParams();
  const email = sp.get("email");
  const phone = sp.get("phone");
  const nav = useNavigate();
  const [regs, setRegs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});

  const load = async () => {
    try {
      const { data } = await api.post("/participants/lookup", { email, phone });
      setRegs(data.registrations);
      if (!selected && data.registrations.length > 0) setSelected(data.registrations[0]);
      else if (selected) {
        const updated = data.registrations.find((r) => r.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch (e) {
      toast.error("Data tidak ditemukan");
      nav("/check");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!email || !phone) { nav("/check"); return; }
    api.get("/config").then((r) => setConfig(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (<div className="min-h-screen"><Nav /><div className="p-12 text-white/60">Loading...</div></div>);

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">RUNNER DASHBOARD</div>
            <h1 className="font-display text-3xl md:text-4xl mt-2 break-all">{email}</h1>
          </div>
          <button onClick={load} className="btn-outline text-xs" data-testid="refresh-btn">REFRESH</button>
        </div>

        <div className="mt-8 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-4 space-y-3 min-w-0">
            {regs.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className={`w-full text-left p-4 card-flat transition-colors ${selected?.id === r.id ? "border-[#FF3B30]" : "hover:border-white/30"}`}
                data-testid={`reg-item-${r.bib_number}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl">#{r.bib_number}</div>
                  <StatusBadge status={r.payment_status} />
                </div>
                <div className="mt-1 text-xs text-[#FF3B30] tracking-widest font-display">{r.event_name}</div>
                <div className="mt-1 text-sm text-white/70">{r.category_name} · {r.category_distance_km} KM</div>
                <div className="mt-1 text-xs text-white/40 tracking-widest">{new Date(r.created_at).toLocaleDateString("id-ID")}</div>
              </button>
            ))}
          </div>
          <div className="md:col-span-8 min-w-0">
            {selected && <RegistrationDetail reg={selected} config={config} email={email} phone={phone} onChanged={load} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationDetail({ reg, config, email, phone, onChanged }) {
  const paid = reg.payment_status === "verified";
  const finished = reg.result_status === "verified";
  const [proofPath, setProofPath] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);

  const submitProof = async () => {
    if (!proofPath) return toast.error("Unggah bukti terlebih dahulu");
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("path", proofPath);
      await api.post(`/registrations/${reg.id}/payment-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti diunggah. Menunggu verifikasi.");
      setProofPath("");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingProof(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`detail-${reg.bib_number}`}>
      {/* E-BIB */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-white/50 font-display">OFFICIAL E-BIB</div>
            <div className="text-xs text-white/50">
              {paid ? "Active · silakan lari!" : "Terkunci · lakukan verifikasi pembayaran"}
            </div>
          </div>
          {paid && (
            <button onClick={() => downloadElementAsPng(`ebib-${reg.id}`, `EBIB-${reg.bib_number}.png`)} className="btn-blaze text-xs inline-flex items-center gap-2" data-testid="download-ebib">
              <DownloadSimple size={16} /> DOWNLOAD PNG
            </button>
          )}
        </div>
        <div className={`mt-6 transition-opacity ${paid ? "opacity-100" : "opacity-40"}`}>
          <ScaledPreview width={600} height={400}>
            <EBib registration={reg} eventName={reg.event_name || config.event_name} backgroundPath={reg.bib_background_path} sponsorLogos={config.sponsor_logos || []} />
          </ScaledPreview>
        </div>
      </div>

      {/* Payment */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] tracking-widest text-white/50 font-display">PAYMENT STATUS</div>
          <StatusBadge status={reg.payment_status} />
        </div>
        <div className="mt-2 text-sm text-white/70">
          Subtotal: <span className="font-display text-lg">{formatIDR(reg.total_price_idr ?? reg.category_price_idr)}</span>
          {(reg.shipping_courier || reg.region_name) && <span className="text-xs text-white/50"> · ongkir {reg.shipping_courier ? `${reg.shipping_courier.toUpperCase()} ${reg.shipping_service || ""}` : reg.region_name}{reg.shipping_fee_idr ? ` (${formatIDR(reg.shipping_fee_idr)})` : ""}</span>}
        </div>
        <div className="mt-2 text-sm text-white/70" data-testid={`dash-unique-code-${reg.bib_number}`}>
          <span className="font-display">{reg.payment_status === "verified" ? "TOTAL DIBAYAR: " : "TOTAL YANG HARUS DIBAYAR: "}<span className="text-xl text-[#FF3B30]" data-testid={`dash-total-transfer-${reg.bib_number}`}>{formatIDR((reg.total_transfer_idr ?? (reg.total_price_idr ?? reg.category_price_idr) + (reg.unique_code ?? 0)))}</span></span>
          {reg.unique_code != null && reg.payment_status !== "verified" && (
            <span className="text-xs text-white/50"> · sudah termasuk kode unik <span className="font-mono-num text-[#FF3B30]">{String(reg.unique_code).padStart(3, "0")}</span> — transfer TEPAT hingga 3 digit terakhir</span>
          )}
        </div>
        {reg.payment_status === "rejected" && reg.payment_notes && (
          <div className="mt-3 border border-red-900/40 bg-red-500/10 text-red-300 p-3 text-sm">
            Ditolak: {reg.payment_notes}
          </div>
        )}
        {reg.payment_status === "pending" && (
          <div className="mt-3 border border-amber-500/40 bg-amber-500/10 text-amber-300 p-3 text-sm flex items-start gap-2" data-testid="dash-payment-pending-note">
            <Clock size={16} className="mt-0.5 shrink-0" />
            <span>Bukti pembayaran Anda sudah kami terima. Data pembayaran Anda akan diverifikasi oleh admin dalam <span className="font-display tracking-wide">1x24 JAM</span>. E-BIB Anda aktif otomatis setelah pembayaran terverifikasi.</span>
          </div>
        )}
        {reg.payment_status === "verified" && (
          <div className="mt-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 text-sm flex items-start gap-2" data-testid="dash-shipment">
            <Package size={16} className="mt-0.5 shrink-0 text-[#FF3B30]" />
            {reg.tracking_number ? (
              <span>RACE PACK <span className="font-display tracking-wide text-emerald-400">DIKIRIM</span> — No. Resi: <span className="font-mono-num text-white">{reg.tracking_number}</span>. Pantau lewat situs/aplikasi kurir.</span>
            ) : (
              <span>RACE PACK: <span className="font-display tracking-wide">SEDANG DIPROSES</span> tim kami. Anda akan menerima email begitu paket dikirim.</span>
            )}
          </div>
        )}
        {(reg.payment_status === "awaiting_proof" || reg.payment_status === "rejected") && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div className="text-xs text-white/60 space-y-4">
              {config.qris_image_path && (
                <div>
                  <div className="font-display tracking-widest text-white/70">PAY VIA QRIS</div>
                  <img src={fileUrl(config.qris_image_path)} alt="QRIS Merchant" className="mt-2 w-full max-w-[220px] border border-[#27272A] bg-white p-2" data-testid="dash-qris-image" />
                </div>
              )}
              {config.bank_transfer_enabled !== false && (
                <div>
                  <div className="font-display tracking-widest text-white/70">PAY VIA BANK TRANSFER</div>
                  <div className="mt-1 font-display text-lg">{config.bank_name}</div>
                  <div className="font-mono-num text-xl text-[#FF3B30]">{config.bank_account_number}</div>
                  <div className="text-white/50">{config.bank_account_holder}</div>
                </div>
              )}
              {!config.qris_image_path && config.bank_transfer_enabled === false && (
                <div className="text-sm text-amber-400">Metode pembayaran belum tersedia. Silakan hubungi panitia.</div>
              )}
            </div>
            <div>
              <Uploader purpose="payment_proof" value={proofPath} onUploaded={setProofPath} label="Upload Bukti Transfer" testid="dash-proof" />
              <button onClick={submitProof} disabled={uploadingProof || !proofPath} className="btn-blaze mt-3 text-xs" data-testid="dash-proof-submit">
                {uploadingProof ? "..." : "SUBMIT PROOF"}
              </button>
            </div>
          </div>
        )}
        {reg.payment_proof_path && (
          <div className="mt-4">
            <div className="text-[10px] tracking-widest text-white/50 mb-1">CURRENT PROOF</div>
            <img src={fileUrl(reg.payment_proof_path)} alt="proof" className="max-h-40 border border-[#27272A]" />
          </div>
        )}
      </div>

      {/* Result submission */}
      {paid && <ResultSection reg={reg} email={email} phone={phone} onChanged={onChanged} />}

      {/* Certificate */}
      {finished && (
        <div className="card-flat p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-widest text-white/50 font-display">E-CERTIFICATE</div>
              <div className="text-xs text-white/50">Congratulations, finisher.</div>
            </div>
            <button onClick={() => downloadElementAsPng(`ecert-${reg.id}`, `Certificate-${reg.bib_number}.png`, 2700)} className="btn-blaze text-xs inline-flex items-center gap-2" data-testid="download-cert">
              <DownloadSimple size={16} /> DOWNLOAD PNG
            </button>
          </div>
          <div className="mt-6">
            <ScaledPreview width={900} height={637}>
              <ECertificate registration={reg} eventName={reg.event_name || config.event_name} backgroundPath={reg.cert_background_path} sponsorLogos={config.sponsor_logos || []} />
            </ScaledPreview>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultSection({ reg, email, phone, onChanged }) {
  const [h, setH] = useState("");
  const [m, setM] = useState("");
  const [s, setS] = useState("");
  const [distance, setDistance] = useState(reg.category_distance_km);
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (attachments.length === 0) return toast.error("Lampirkan minimal 1 bukti hasil lari (maks 5)");
    const total = parseHMS(h, m, s);
    if (total <= 0) return toast.error("Isi waktu lari (jam:menit:detik)");
    const minDist = (reg.category_distance_km || 0) * 0.98;
    if (minDist > 0 && parseFloat(distance) < minDist) return toast.error(`Jarak minimal ${reg.category_distance_km} KM untuk kategori ${reg.category_name} (toleransi GPS 2%)`);
    setSubmitting(true);
    try {
      await api.post(`/registrations/${reg.id}/result?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`, {
        time_seconds: total,
        distance_km: parseFloat(distance),
        notes,
        attachment_paths: attachments,
        finisher_photo_path: selfie || "",
      });
      toast.success("Hasil dikirim. Menunggu verifikasi.");
      setAttachments([]);
      setSelfie(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = reg.result_status === "not_submitted" || reg.result_status === "rejected";

  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-widest text-white/50 font-display">RUN RESULT</div>
          <div className="text-xs text-white/50">Submit hasil lari untuk penerbitan sertifikat.</div>
        </div>
        <StatusBadge status={reg.result_status} />
      </div>
      {reg.result_time_seconds != null && (
        <div className="mt-4 grid grid-cols-3 gap-4">
          <Stat icon={Timer} label="TIME" value={formatSeconds(reg.result_time_seconds)} />
          <Stat icon={CheckCircle} label="DISTANCE" value={`${reg.result_distance_km} KM`} />
          <Stat icon={Clock} label="STATUS" value={reg.result_status?.toUpperCase()} />
        </div>
      )}
      {reg.result_status === "rejected" && reg.result_admin_notes && (
        <div className="mt-3 border border-red-900/40 bg-red-500/10 text-red-300 p-3 text-sm">
          Rejected: {reg.result_admin_notes}
        </div>
      )}
      {canSubmit && (
        <div className="mt-6 space-y-4">
          <div>
            <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">Finish Time</div>
            <div className="flex gap-2">
              <input type="number" min="0" max="23" value={h} onChange={(e) => setH(e.target.value)} className="tactical-input" placeholder="HH" data-testid="res-h" />
              <input type="number" min="0" max="59" value={m} onChange={(e) => setM(e.target.value)} className="tactical-input" placeholder="MM" data-testid="res-m" />
              <input type="number" min="0" max="59" value={s} onChange={(e) => setS(e.target.value)} className="tactical-input" placeholder="SS" data-testid="res-s" />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">Distance Recorded (KM)</div>
            <input type="number" step="0.01" value={distance} onChange={(e) => setDistance(e.target.value)} className="tactical-input" data-testid="res-distance" />
            <p className="mt-1 text-[11px] text-white/40">Minimal {reg.category_distance_km} KM sesuai kategori {reg.category_name} (toleransi GPS 2%). Jarak lebih tetap diterima — ranking leaderboard dihitung dari pace (menit/km).</p>
          </div>
          <MultiUploader purpose="result" values={attachments} onChange={setAttachments} label="Lampiran Bukti Hasil (Strava / Nike / dll)" testid="res-upload" max={5} />
          <div>
            <Uploader purpose="result" value={selfie} onUploaded={setSelfie} label="Foto Selfie Finisher (opsional)" testid="res-selfie" accept="image/*" />
            <p className="mt-1 text-[11px] text-white/40">Foto ini akan tampil di Finisher Wall publik setelah hasil Anda terverifikasi.</p>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">Notes (optional)</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="tactical-input" rows={2} placeholder="e.g. Route link, weather..." data-testid="res-notes" />
          </div>
          <button onClick={submit} disabled={submitting} className="btn-blaze w-full" data-testid="res-submit">
            {submitting ? "..." : "SUBMIT RESULT FOR VERIFICATION"}
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border-l-2 border-[#FF3B30] pl-4">
      <div className="flex items-center gap-2 text-[10px] tracking-widest text-white/50 font-display">
        <Icon size={14} /> {label}
      </div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
